import React, { FC, ReactNode, useContext, useMemo } from 'react';
import { RouteProps, Navigate, useLocation } from 'react-router-dom';
import authService from 'services/authService';
import credentialsService from 'services/credentialsService';
import { Role } from 'models/User';
import AuthContext from 'providers/Auth/Auth.context';
import useQueryParams from 'hooks/useQueryParams';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { BUSINESS_ROUTE, LOGIN_ROUTE } from 'data/global';

export type EnhancedRouteProps = {
  authorized?: boolean;
  onlyPublic?: boolean;
  authorizedRoles?: Array<Role>;
} & Omit<RouteProps, 'element'> & {
    element: FC | ReactNode;
  };

const EnhancedRoute: React.FC<EnhancedRouteProps> = (props) => {
  const {
    authorized = false,
    onlyPublic = false,
    element,
    authorizedRoles,
  } = props;
  const { isLoggedIn } = useContext(AuthContext);
  const { currentUser } = useContext(CurrentUserContext);

  const user = useMemo(() => currentUser, [currentUser]);

  const {
    params: { fullSlugName },
  } = useQueryParams();

  const { pathname } = useLocation();

  const finalRoute = useMemo(() => {
    if (fullSlugName) {
      if (isLoggedIn) {
        return <Navigate to={fullSlugName} replace />;
      }
    }

    if ((authorized || authorizedRoles) && !isLoggedIn) {
      return (
        <Navigate
          to={{ pathname: LOGIN_ROUTE }}
          state={{ from: pathname }}
          replace
        />
      );
    }

    if (onlyPublic && isLoggedIn) {
      return <Navigate to={BUSINESS_ROUTE} />;
    }

    if (authorizedRoles) {
      const userAllowed = authService.checkRolesForUser(user, authorizedRoles);
      if (!userAllowed) credentialsService.removeAuthBody();
    }

    if (typeof element === 'function') {
      const Component = element;

      return <Component />;
    }
    return element as React.ReactElement;
  }, [
    authorized,
    authorizedRoles,
    isLoggedIn,
    onlyPublic,
    pathname,
    user,
    element,
    fullSlugName,
  ]);

  return finalRoute;
};

export default EnhancedRoute;
